<template>
    <div>
        <div id="exportable-content">
            <div class="mx-auto max-w-screen-2xl px-6">
                <Hero :title="PRICING_HERO.title" :subtitle="PRICING_HERO.subtitle"
                    :highlight-words="['transparent']" />

                <!-- Term Toggle -->
                <div class="mt-16 flex justify-center">
                    <fieldset aria-label="Payment frequency">
                        <div
                            class="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs/5 font-semibold ring-1 ring-inset ring-gray-700">
                            <label class="cursor-pointer rounded-full px-2.5 py-1" :class="[
                                term === 'Monthly'
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-400'
                            ]">
                                <input type="radio" v-model="term" name="frequency" value="Monthly" class="sr-only">
                                <span>Monthly</span>
                            </label>
                            <label class="cursor-pointer rounded-full px-2.5 py-1" :class="[
                                term === 'Yearly'
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-400'
                            ]">
                                <input type="radio" v-model="term" name="frequency" value="Yearly" class="sr-only">
                                <span>Yearly (17% off)</span>
                            </label>
                        </div>
                    </fieldset>
                </div>

                <!-- Pricing Plans Table -->
                <div
                    class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-6xl lg:mx-0 lg:grid-cols-4 xl:max-w-full">
                    <Column v-for="plan in activePlans" :key="plan.name" :column="{
                        ...plan,
                        price: getDisplayedPlanPrice(plan),
                        priceUnit: term === 'Monthly' ? '/month' : '/year',
                        cta: usePricingCTA(plan.name),
                        isHighlighted: plan.name.toLowerCase() === 'pro'
                    }" :is-pricing="true" :show-description="true" @column-click="handleColumnClick">
                        <template #features>
                            <h4 class="text-sm font-medium text-gray-400">{{ getPlanFeatures(plan).subtitle }}</h4>
                            <ul role="list" class="mt-4 space-y-3 text-sm/6 text-gray-400">
                                <li v-for="(feature, index) in getPlanFeatures(plan).features" :key="index"
                                    class="flex gap-x-3">
                                    <Icon name="material-symbols:check" class="h-6 w-5 flex-none text-blue-400" />
                                    <span>{{ feature }}</span>
                                </li>
                            </ul>
                            <!-- <div v-if="plan.name === 'Start'" class="text-sm text-gray-400 flex mt-8 items-start">

                            <Icon name="material-symbols:info" class="h-6 w-5 flex-none text-blue-400 mr-2" />
                            <span>
                                The Start plan will be sunsetted on August 1, 2025.
                            </span>

                        </div> -->
                        </template>
                    </Column>
                    <Column :is-pricing="true" :show-description="true" @column-click="handleColumnClick" :column="{
                        name: 'Enterprise',
                        description: 'For enterprise product teams requiring a custom plan.',
                        price: 'custom',
                        priceUnit: '/year',
                        cta: usePricingCTA('Enterprise'),
                    }">
                        <template #features>
                            <h4 class="text-sm font-medium text-gray-400">Enterprise-ready infrastructure and support,
                                customized for your needs:</h4>
                            <ul role="list" class="mt-4 space-y-3 text-sm/6 text-gray-400">
                                <li v-for="(feature, index) in ['Single tenant / private cloud / dedicated cloud / on-prem', 'Prioritized 24/7 support', 'Custom support hours', 'Accelerated integration roadmap', 'Enterprise-grade scalability', 'Usagage-based plans OR connections-based plans']"
                                    :key="index" class="flex gap-x-3">
                                    <Icon name="material-symbols:check" class="h-6 w-5 flex-none text-blue-400" />
                                    <span>{{ feature }}</span>
                                </li>
                            </ul>
                        </template>
                    </Column>
                </div>

                <!-- Compare Plans Button -->
                <div class="flex justify-center mt-8">
                    <button @click="scrollToPricingBreakdown"
                        class="flex items-center gap-2 px-3 py-2 text-sm/6 font-semibold rounded-md cursor-pointer text-white ring-1 ring-inset ring-gray-500/20 hover:text-blue-400 transition-colors">
                        Compare Plans
                        <Icon name="material-symbols:arrow-downward" class="text-lg" />
                    </button>
                </div>

                <!-- Pricing Calculator -->
                <PricingCalculator :plans="activePlans" @linkClick="handleFeatureClick" />

                <h2
                    class="lg:mt-24 mb-12 text-balance text-2xl text-center font-semibold tracking-tight text-white sm:text-2xl pricing-breakdown">
                    Compare Plans
                </h2>

                <!-- Comparison Table -->
                <ComparisonTable class="my-12" :columns="activePlans.map(plan => ({
                    ...plan,
                    price: getDisplayedPlanPrice(plan),
                    priceUnit: term === 'Monthly' ? '/month' : '/year',
                    cta: usePricingCTA(plan.name),
                    isHighlighted: plan.name.toLowerCase() === 'scale',
                    key: plan.name
                }))" :sections="PLAN_FEATURES_EXPANDED" :is-pricing-table="true" :highlight-column-index="1"
                    @column-click="handleColumnClick" />

                <div class="my-8 sm:my-12 text-center">
                    <p class="text-gray-400">
                        <NuxtLink to="/contact" class="text-blue-500 hover:text-blue-400">Contact us</NuxtLink>
                        for custom limits, enterprise pricing, and configurations. Options include private cloud,
                        dedicated
                        cloud, 24/7 support, and accelerated integration roadmap.
                    </p>
                </div>

                <!-- FAQ Section -->
                <div class="mt-24">
                    <a class="anchor" name="faq" id="faq"></a>
                    <h2 class="text-2xl font-bold text-white border-b border-gray-700 pb-4">
                        Frequently Asked Questions
                    </h2>

                    <Accordion v-model="activeAccordionIndex" :labels="PRICING_FAQS.map(faq => faq.title)" class="mt-8">
                        <template v-for="(faq, index) of PRICING_FAQS" :key="index" v-slot:[getVSlot(index)]>
                            <a v-if="faq.id" class="anchor" :name="faq.id" :id="faq.id" />
                            <span v-html="faq.answer.replace(/\n/g, '<br>')" class="text-gray-400" />
                        </template>
                    </Accordion>
                </div>

                <p class="mt-12 text-center text-xs text-gray-500">Last updated: June 2026</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { PLANS, type IPlan } from '~/models/UnifiedInternal';
import { PRICING_HERO } from '~/services/pricing';
import { PRICING_FAQS } from '~/constants/pricing-faq';
import { PLAN_FEATURES_SUMMARY, PLAN_FEATURES_EXPANDED, PlanName } from '~/constants/pricing-plan-features';

export default {
    setup() {
        const faqJsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: PRICING_FAQS.map((faq) => ({
                '@type': 'Question',
                name: faq.title,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer.replace(/<[^>]*>/g, '').trim(),
                },
            })),
        };

        useSEO({
            title: 'Pricing',
            description:
                'Add API-first integrations with a Unified API that scales with you. Start with 2,500 free API requests on a tester account and choose from one of our flexible, usage-based plans when you launch.',
            jsonLd: faqJsonLd,
        });

        return {
            PRICING_HERO,
            PRICING_FAQS,
        };
    },

    data() {
        return {
            term: 'Monthly' as 'Monthly' | 'Yearly',
            activeAccordionIndex: 0,
            PLAN_FEATURES_EXPANDED
        };
    },

    computed: {
        activePlans(): IPlan[] {
            const plans = PLANS.filter(plan => plan.is_active && plan.name !== PlanName.Test);
            plans.sort((a, b) => (a.min_price ?? 0) - (b.min_price ?? 0));

            return plans;
        }
    },

    methods: {
        getVSlot(index: number) {
            return `section-${index + 1}`
        },
        handleFeatureClick(id: string) {
            this.activeAccordionIndex = this.PRICING_FAQS.findIndex(faq => faq.id === id);
        },

        getPlanFeatures(plan: IPlan) {
            const planName = plan.name as PlanName;
            return PLAN_FEATURES_SUMMARY[planName] || PLAN_FEATURES_SUMMARY[PlanName.Test];
        },

        getDisplayedPlanPrice(plan: IPlan): number {
            const monthlyPrice = plan.min_price ?? 0;
            const annualPrice = plan.min_annual_price ?? 0;

            if (this.term === 'Monthly') return monthlyPrice;

            // Guard against bad data where yearly is missing or <= monthly.
            if (!annualPrice || annualPrice <= monthlyPrice) {
                return Math.round(monthlyPrice * 10);
            }

            return annualPrice;
        },

        scrollToPricingBreakdown() {
            document.querySelector('.pricing-breakdown')?.scrollIntoView({ behavior: 'smooth' });
        },

        handleColumnClick(column: any) {
            const planName = column.name.toLowerCase();
            if (planName === 'enterprise' || planName === 'scale') {
                window.location.href = '/contact';
            } else {
                window.location.href = `https://app.unified.to?plan=${column.name}`;
            }
        },

        usePricingCTA(name: string): string {
            const planName = name.toLowerCase();
            // if (planName === 'test' || planName === 'grow') return 'Start for free for 30 days';
            if (planName === 'enterprise' || planName === 'scale') return 'Book a demo';
            return 'Start 30 days for free';
        }
    }
};
</script>

<style scoped>
.pricing-page :deep(.anchor) {
    display: block;
    position: relative;
    top: -100px;
    visibility: hidden;
}
</style>